import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { cloneDeep } from 'src/ep-utils';
//import { BaseState } from '../base/base-state'
import { EditStatusType, StateHelperService } from '../../../services/proprietary/state-helper.service';
import { RemoveSubLocationFromArticle, ReplaceLocationOnArticles } from './article.actions';
import { ContractObjectDefaults } from './contract-object.defaults';
import { AddLocationPointer, RemoveLocationPointer } from './contract.actions';
import {
    AddLocation,
    AddSubLocationLink,
    CloneLocation,
    DeleteLocation,
    RemoveAllSubLocationLinks,
    RemoveSubLocationLink,
    RestoreLocation,
    SetCurrentLocation,
    UpdateLocation
} from './location.actions';

export interface LocationStateModel {
    location: any;
    currentID: string;
}

@State<LocationStateModel>({
    name: 'locationState',
    defaults: {
        location: {},
        currentID: null
    }
})
@Injectable()
export class LocationState {
    constructor(private helper: StateHelperService) { }

    //******* SELECTORS *********

    @Selector()
    static locationCodeTable(state: LocationStateModel) {
        //return all locations in Code table format
        let result: any = [{ Code: '', Code_Desc: '' }]; //include empty as option for clearing.
        for (var i in state.location) {
            let loc = state.location[i];
            if (
                loc.Edit_Status &&
                (loc.Edit_Status == EditStatusType.Delete || loc.Edit_Status == EditStatusType.PreviousDelete)
            ) {
                //don't include deleted locations in code table list
                continue;
            }
            result.push({
                Code: loc.Loc_Num,
                Code_Desc:
                    'Loc# ' +
                    loc.Loc_Num +
                    (loc.Address_Line_1 ? ': ' + loc.Address_Line_1 : '') +
                    (loc.State ? ' (' + loc.State + ')' : '')
            });
        }

        result.sort((a, b) => a.Code - b.Code);

        return result;
    }

    @Selector()
    static locationIDCodeTable(state: LocationStateModel) {
        //return all locations in Code table format using Loc_ID as the Code
        let locations: any = [];
        let result: any = [{ Code: '', Code_Desc: '' }]; //include empty as option for clearing.
        for (var i in state.location) {
            let loc = state.location[i];
            if (
                loc.Edit_Status &&
                (loc.Edit_Status == EditStatusType.Delete || loc.Edit_Status == EditStatusType.PreviousDelete)
            ) {
                //don't include deleted locations in code table list
                continue;
            }

            locations.push(loc);
        }

        locations.sort((a, b) => a.Loc_Num - b.Loc_Num);
        for (var i in locations) {
            let loc = locations[i];
            result.push({
                Code: loc.Loc_ID,
                Code_Desc:
                    'Loc# ' +
                    loc.Loc_Num +
                    (loc.Address_Line_1 ? ': ' + loc.Address_Line_1 : '') +
                    (loc.State ? ' (' + loc.State + ')' : '')
            });
        }

        return result;
    }

    @Selector()
    static locationByLocNum(state: LocationStateModel) {
        return (locNum: string) => {
            if (locNum) {
                for (var i in state.location) {
                    if (state.location[i].Loc_Num == locNum) {
                        return state.location[i];
                    }
                }
            }
            return {};
        };
    }

    @Selector()
    static locationByLocID(state: LocationStateModel) {
        return (locID: string) => {
            if (locID) {
                for (var i in state.location) {
                    if (state.location[i].Loc_ID == locID) {
                        return state.location[i];
                    }
                }
            }
            return {};
        };
    }

    @Selector()
    static currentLocation(state: LocationStateModel) {
        return state.location[state.currentID] || {};
    }

    @Selector()
    static locations(state: LocationStateModel) {
        let result: any = [];
        for (var i in state.location) {
            result.push(state.location[i]);
        }
        return result;
    }

    @Selector()
    static locationCount(state: LocationStateModel) {
        //return article count of a particular type
        let count = 0;
        for (var i in state.location) {
            count++;
        }
        return count;
    }

    @Selector()
    static locIDByLocNum(state: LocationStateModel) {
        return (locNum: string) => {
            if (locNum) {
                for (var i in state.location) {
                    if (state.location[i].Loc_Num == locNum) {
                        return state.location[i].Loc_ID;
                    }
                }
            }
            return '';
        };
    }

    //******  ACTIONS  ******

    @Action(SetCurrentLocation)
    setCurrentLocation(sc: StateContext<LocationStateModel>, action: SetCurrentLocation) {
        const state = sc.getState();
        let locID = null;
        if (action.location) {
            if (action.location.Loc_ID) {
                locID = action.location.Loc_ID;
            } else if (action.location.Loc_Num) {
                for (var i in state.location) {
                    if (state.location[i].Loc_Num == action.location.Loc_Num) {
                        locID = state.location[i].Loc_ID;
                        break;
                    }
                }
            }
        }
        sc.patchState({ currentID: locID });
    }

    @Action(UpdateLocation)
    updateLocation(sc: StateContext<LocationStateModel>, action: UpdateLocation) {
        const state = sc.getState();
        const locations = state.location;
        let locID = action.location.Loc_ID;
        let newLocNum = action.location.Loc_Num;
        let oldLocNum = state.location[locID].Loc_Num;
        let updateLoc: any = { ...action.location }; //need DEEP copy?  -- subloc list????
        this.helper.setItemChanged(updateLoc, state.location[locID].Edit_Status);
        let locNumChanged: boolean = false;

        if (newLocNum && newLocNum != '' && oldLocNum && oldLocNum != '' && newLocNum != oldLocNum) {
            if (parseInt(newLocNum, 10) < 1) {
                newLocNum = '-1';
                updateLoc.Loc_Num = '-1';
            }

            let currentLocations: any = [];
            for (var i in locations) {
                currentLocations.push(locations[i]);
            }

            let idx = currentLocations.map((x) => x.Loc_Num).indexOf(newLocNum);

            if (idx == -1 && newLocNum == '-1') {
                idx = currentLocations.map((x) => x.Loc_Num).indexOf('0');
            }

            if (idx === -1) {
                locNumChanged = true;
            } else {
                // Not unique Loc_Num, reject the change
                updateLoc.Loc_Num = oldLocNum;
            }
        }

        sc.patchState({
            location: {
                ...state.location,
                [locID]: {
                    ...state.location[locID],
                    ...updateLoc
                }
            }
        });

        // if locnum was changed then update on all articles
        if (locNumChanged) {
            sc.dispatch(new ReplaceLocationOnArticles(oldLocNum, newLocNum));
        }
    }

    @Action(DeleteLocation)
    deleteLocation(sc: StateContext<LocationStateModel>, action: DeleteLocation) {
        const state = sc.getState();
        let locNum = state.location[action.locID].Loc_Num;
        let location = { ...state.location }; //shallow copy location from state
        this.helper.setItemDeleted(location, action.locID);
        sc.patchState({ location: location, currentID: null });
        sc.dispatch(new ReplaceLocationOnArticles(locNum, '')); // unlink all articles attached to this location
        if (action.locID < 0) {
            sc.dispatch(new RemoveLocationPointer(action.locID.toString()));
        }
    }

    @Action(RestoreLocation)
    restoreLocation(sc: StateContext<LocationStateModel>, action: RestoreLocation) {
        const state = sc.getState();
        let location = { ...state.location }; //shallow copy location from state
        this.helper.setItemRestored(location, action.locID);
        sc.patchState({ location: location });
    }

    @Action(CloneLocation)
    cloneLocation(sc: StateContext<LocationStateModel>, action: CloneLocation) {
        const state = sc.getState();
        let srcID = null;

        //if locNum provided then lookup by locnum
        if (action.locNum) {
            if (action.locID) {
                throw 'locID and locNum cannot both be provided!';
            }
            for (var i in state.location) {
                if (state.location[i].Loc_Num == action.locNum) {
                    srcID = i;
                }
            }
        } else {
            srcID = action.locID;
        }
        if (!srcID) {
            throw 'Source location not found in store.';
        }

        let newID = this.helper.getTempID();
        let newLocNum = this.helper.getNextItemNumber(state.location, 'Loc_Num').toString();
        let newLoc = cloneDeep(state.location[srcID]);
        this.helper.setItemAdded(newLoc);
        newLoc.Loc_ID = newID;
        newLoc.Loc_Num = newLocNum;

        // remove sub locations from cloned location
        if (newLoc.Sub_Location) {
            delete newLoc.Sub_Location;
        }

        sc.patchState({
            currentID: newID,
            location: { ...state.location, [newID]: newLoc }
        });

        sc.dispatch(new AddLocationPointer(newID)); // add location to list array on contract node (for denormalization)
    }

    @Action(AddLocation)
    addLocation(sc: StateContext<LocationStateModel>, action: AddLocation) {
        const state = sc.getState();
        let newID = this.helper.getTempID();
        let newLocNum = this.helper.getNextItemNumber(state.location, 'Loc_Num').toString();
        let newLoc = this.helper.createDefaultItem(ContractObjectDefaults, 'Contract_Location', action.location);
        this.helper.setItemAdded(newLoc);
        newLoc.Loc_ID = newID;
        newLoc.Loc_Num = newLocNum;
        newLoc.Sub_Location = [];

        sc.patchState({
            currentID: newID,
            location: { ...state.location, [newID]: newLoc }
        });

        sc.dispatch(new AddLocationPointer(newID)); // add location to list array on contract node (for denormalization)
    }

    @Action(AddSubLocationLink)
    addSubLocationLink(sc: StateContext<LocationStateModel>, action: AddSubLocationLink) {
        const state = sc.getState();

        let locID = action.locID;
        if (!locID) {
            locID = parseInt(state.currentID, 10);
        }

        let updateLocation: any = cloneDeep(state.location[locID]);
        this.helper.setItemChanged(updateLocation, updateLocation.Edit_Status);

        if (!updateLocation.Sub_Location) {
            updateLocation.Sub_Location = [];
        }

        updateLocation.Sub_Location = updateLocation.Sub_Location.concat(action.subLocID.toString());

        sc.patchState({
            location: {
                ...state.location,
                [locID]: { ...state.location[locID], ...updateLocation }
            }
        });
    }

    @Action(RemoveSubLocationLink)
    removeSubLocationLink(sc: StateContext<LocationStateModel>, action: RemoveSubLocationLink) {
        const state = sc.getState();

        let locID = action.locID;
        let updateLocation: any = cloneDeep(state.location[locID]);
        let locNum = state.location[locID].Loc_Num;
        let subLocs: any[] = [...updateLocation.Sub_Location];
        if (action.subLocID < 0) {
            if (subLocs && subLocs.length === 1) {
                updateLocation.Sub_Location = [];
            } else {
                updateLocation.Sub_Location = this.helper.removeArrayItemByValue(subLocs, action.subLocID.toString());
            }
        } else {
            updateLocation.Sub_Location = [...subLocs];
        }
        this.helper.setItemChanged(updateLocation, updateLocation.Edit_Status);

        sc.patchState({
            location: {
                ...state.location,
                [locID]: { ...state.location[locID], ...updateLocation }
            }
        });
        sc.dispatch(new RemoveSubLocationFromArticle(locNum, action.subLocNum));
    }

    @Action(RemoveAllSubLocationLinks)
    removeAllSubLocationLinks(sc: StateContext<LocationStateModel>, action: RemoveAllSubLocationLinks) {
        const state = sc.getState();
        let locID = action.locID;
        let subLocIDs = state.location[locID].Sub_Location;
        let updateLocation: any = cloneDeep(state.location[locID]);

        if (!updateLocation.Sub_Location) {
            updateLocation.Sub_Location = [];
        }

        let subLocs: any[] = [...updateLocation.Sub_Location];
        if (subLocIDs && subLocIDs.length > 0) {
            for (var i = 0; i < subLocIDs.length; i++) {
                if (subLocIDs[i] < 0) {
                    subLocs = this.helper.removeArrayItemByValue(subLocs, subLocIDs[i]);
                } else {
                    subLocs = [...subLocs];
                }
            }
        }
        updateLocation.Sub_Location = [...subLocs];
        sc.patchState({
            location: {
                ...state.location,
                [locID]: { ...state.location[locID], ...updateLocation }
            }
        });
        return sc.dispatch(new DeleteLocation(locID));
    }
}
